import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControlLabel, Switch, Autocomplete, Box, Typography, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { geocodeAddress } from 'utils/geocoding';

function AddOrEditDialog({ open, handleClose, handleSave, initialData, allData }) {
  const [data, setData] = useState({ ...initialData });
  const [regeocoding, setRegeocoding] = useState(false);
  const [regeocodeMsg, setRegeocodeMsg] = useState(null);

  useEffect(() => {
    setData({ ...initialData });
    setRegeocodeMsg(null);
  }, [initialData]);

  const handleRegeocode = async () => {
    setRegeocoding(true);
    setRegeocodeMsg(null);
    try {
      const hit = await geocodeAddress({
        address: data.address,
        address2: data.address2,
        city: data.city,
        state: data.state,
        zip: data.zip,
      });
      if (hit) {
        setData({ ...data, lat: hit.lat, lon: hit.lon });
        setRegeocodeMsg({ ok: true, text: `Found: ${hit.lat.toFixed(4)}, ${hit.lon.toFixed(4)}` });
      } else {
        setRegeocodeMsg({ ok: false, text: 'No confident match — check the city/state/zip values.' });
      }
    } catch (err) {
      setRegeocodeMsg({ ok: false, text: `Lookup failed: ${err.message}` });
    } finally {
      setRegeocoding(false);
    }
  };

  // Extract unique categories from allData
  const categories = useMemo(() => {
    const categorySet = new Set();
    if (allData) {
      allData.forEach(addr => {
        if (addr.category) {
          categorySet.add(addr.category);
        }
      });
    }
    return Array.from(categorySet).sort();
  }, [allData]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setData({ ...data, [e.target.name]: value });
  };

  const handleFormSubmit = async () => {
    let addressData = { ...data };

    // Geocode if address changed or it's a new entry
    const hasAddressChanged = initialData.address !== data.address ||
                              initialData.address2 !== data.address2 ||
                              initialData.city !== data.city ||
                              initialData.state !== data.state ||
                              initialData.zip !== data.zip;

    if (!data.id || hasAddressChanged) {
      try {
        const hit = await geocodeAddress({
          address: data.address,
          address2: data.address2,
          city: data.city,
          state: data.state,
          zip: data.zip,
        });
        if (hit) {
          addressData.lat = hit.lat;
          addressData.lon = hit.lon;
        }
        // If hit is null, the address didn't validate against its own state —
        // we keep whatever lat/lon was already on the record rather than
        // silently saving a wrong match.
      } catch (error) {
      }
    }

    if (data.id) {
      // Edit existing entry
      const updatedData = allData.map(item => item.id === data.id ? addressData : item);
      handleSave(updatedData);
    } else {
      // Add new entry
      const newData = [...allData, { ...addressData, id: Date.now().toString() }]; // Assign a new ID
      handleSave(newData);
    }
    handleClose();
  };


  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{data.address ? 'Edit Address' : 'Add Address'}</DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          name="name"
          label="Name"
          type="text"
          fullWidth
          value={data.name || ''}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="address"
          label="Address 1"
          type="text"
          fullWidth
          value={data.address || ''}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="address2"
          label="Address 2"
          type="text"
          fullWidth
          value={data.address2 || ''}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="city"
          label="City"
          type="text"
          fullWidth
          value={data.city || ''}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="state"
          label="State"
          type="text"
          fullWidth
          value={data.state || ''}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="zip"
          label="Zip Code"
          type="text"
          fullWidth
          value={data.zip || ''}
          onChange={handleChange}
        />
        <Autocomplete
          freeSolo
          options={categories}
          value={data.category || ''}
          onInputChange={(event, newInputValue) => {
            setData({ ...data, category: newInputValue });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              name="category"
              label="Category"
              fullWidth
            />
          )}
        />
        <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleRegeocode}
              disabled={regeocoding}
              startIcon={regeocoding ? <CircularProgress size={14} /> : <RefreshIcon />}
            >
              Re-geocode coords
            </Button>
            <Typography variant="caption" color="text.secondary">
              {typeof data.lat === 'number' && typeof data.lon === 'number'
                ? `Current: ${data.lat.toFixed(4)}, ${data.lon.toFixed(4)}`
                : 'No coords saved'}
            </Typography>
          </Box>
          {regeocodeMsg && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, color: regeocodeMsg.ok ? 'success.main' : 'error.main' }}
            >
              {regeocodeMsg.text}
            </Typography>
          )}
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={data.isLittle || false}
              onChange={handleChange}
              name="isLittle"
              color="primary"
            />
          }
          label="Use Small Font (8pt)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleFormSubmit}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddOrEditDialog;
